import React, { useMemo } from "react"
import { useSelector } from "react-redux"
// eslint-disable-next-line no-restricted-imports -- FIXME: Usages of styled-components should be replaced with components from carotene-core or carotene-core-x. Please do not import styled-components after eslint rule added on 3/21/2025.
import styled from "styled-components"
import { useCurrentUser } from "../../context/user/UserContext"
import { DbgPaths, Paths } from "#/utils/Paths"
import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl"
import { getCurrencyCode } from "#/redux/reducers/companyInfo"
import { getCategoryName } from "../reimbursements/ReimbursementCategoryMessages"
import {
  Box,
  InternalIcon,
  IconButton,
  InfoIcon,
  Link,
  Typography,
  IconWithBackground,
  MoneyIcon,
  Tile,
  Stack,
  useTheme,
  SurfaceColor
} from "@carrotfertility/carotene-core"
import { PlanInfoBadgeContainer } from "../../containers/planinfo/PlanInfoBadgeContainer"
import { getCategoryCoverage } from "#/utils/CategoryCoverage"
import { BenefitType, Subsidy } from "#/lib/carrot-api/types/Subsidy"
import { useToggle } from "#/utils/Hooks"
import { CategoryInfoDialog } from "./AvailableFundsCategoryInfoDialog"
import { CategoryAccordion } from "./CategoryAccordion"
import { countCategories, getVisibleSubsidies, groupByCategory, isCreditMember } from "./subsidyCategoryGroups"

const optionsKey = "available-funds-module"
const availableFundsOptions = JSON.parse(localStorage.getItem(optionsKey)) || { expanded: {} }

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const CategoryExpands = styled.div`
  display: flex;
  flex-direction: row;
  margin: 1%;
  inline-size: 90%;
  justify-content: space-between;
`

const CategoryBenefitContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`

const AmountValue = ({ amount }: { amount: number }): JSX.Element => {
  const currencyCode = useSelector(getCurrencyCode)
  return (
    <FormattedNumber
      value={Math.max(amount / 100, 0)}
      // eslint-disable-next-line react/style-prop-object -- "currency" is a valid style prop for FormattedNumber
      style="currency"
      currency={currencyCode}
      maximumFractionDigits={0}
      currencyDisplay="narrowSymbol"
    />
  )
}

// Null cap means not reimbursable.
const CreditAmounts = ({ subsidy }: { subsidy: Subsidy }): JSX.Element | null => {
  if (subsidy.creditLifetimeMax == null || subsidy.remainingCredits == null) return null
  return (
    <CategoryExpands>
      <CategoryBenefitContainer>
        <Typography variant="body2" sx={(theme) => ({ fontWeight: "normal", color: theme.palette.text.primary })}>
          {subsidy.remainingCredits < subsidy.creditLifetimeMax ? (
            <FormattedMessage defaultMessage="Remaining lifetime" />
          ) : (
            <FormattedMessage defaultMessage="Available lifetime" />
          )}
        </Typography>
        <Typography variant="h6" sx={(theme) => ({ fontWeight: "bold", color: theme.palette.text.primary })}>
          <FormattedMessage
            defaultMessage="{count, plural, one {# credit} other {# credits}}"
            values={{ count: subsidy.remainingCredits }}
          />
        </Typography>
      </CategoryBenefitContainer>
    </CategoryExpands>
  )
}

const AvailableFundsAmounts = ({
  subsidy,
  showRemainingAnnual,
  showRemainingLifetime
}: {
  subsidy: Subsidy
  showRemainingAnnual: boolean
  showRemainingLifetime: boolean
}) => {
  if (subsidy.benefitType === BenefitType.CREDIT) return <CreditAmounts subsidy={subsidy} />
  const lifetimeAvailable = subsidy.lifetimeAmounts.available
  const yearlyAvailable = subsidy.yearlyAmounts.available
  const hasUsedFunds = subsidy.lifetimeAmounts.claimed > 0
  return (
    <CategoryExpands>
      {showRemainingAnnual && (
        <CategoryBenefitContainer>
          <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
            <Typography
              variant="body2"
              fontWeight="normal"
              color={(theme) => theme.palette.text.primary}
              marginRight={(theme) => theme.tokens.spacing.xxxl}
            >
              {hasUsedFunds ? (
                <FormattedMessage defaultMessage="Remaining annual" />
              ) : (
                <FormattedMessage defaultMessage="Available annual" />
              )}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight="bold" color={(theme) => theme.palette.text.primary}>
            {subsidy.hasUnlimitedBenefit ? (
              <FormattedMessage defaultMessage="Unlimited" />
            ) : (
              <AmountValue amount={yearlyAvailable} />
            )}
          </Typography>
        </CategoryBenefitContainer>
      )}
      {showRemainingLifetime && (
        <CategoryBenefitContainer>
          <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
            <Typography variant="body2" fontWeight="normal" color={(theme) => theme.palette.text.primary}>
              {hasUsedFunds ? (
                <FormattedMessage defaultMessage="Remaining lifetime" />
              ) : (
                <FormattedMessage defaultMessage="Available lifetime" />
              )}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight="bold" color={(theme) => theme.palette.text.primary}>
            {subsidy.hasUnlimitedBenefit || subsidy.lifetimeAmounts.max === null ? (
              <FormattedMessage defaultMessage="Unlimited" />
            ) : (
              <AmountValue amount={lifetimeAvailable} />
            )}
          </Typography>
        </CategoryBenefitContainer>
      )}
    </CategoryExpands>
  )
}

const AvailableFundsByCategory = ({
  subsidies,
  hasQmeCategory,
  showCategoryInfo,
  showRemainingAnnual,
  showRemainingLifetime
}: {
  subsidies: Subsidy[]
  hasQmeCategory: boolean
  showCategoryInfo: boolean
  showRemainingAnnual: boolean
  showRemainingLifetime: boolean
}): JSX.Element => {
  const { employeeId } = useCurrentUser()
  const intl = useIntl()
  const theme = useTheme()
  const [showInfo, toggleInfo] = useToggle(false)
  // eslint-disable-next-line no-prototype-builtins
  if (!availableFundsOptions.expanded.hasOwnProperty(employeeId)) {
    availableFundsOptions.expanded[employeeId] = {}
  }
  const categoryName = intl.formatMessage(
    getCategoryName(subsidies[0].reimbursementCategoryNameMessageKey, hasQmeCategory)
  )
  // Keyed on message key so ids don't vary by locale.
  const accordionId = `accordion-available-funds-${subsidies[0].reimbursementCategoryNameMessageKey}`
  return (
    <>
      <CategoryAccordion
        id={accordionId}
        title={categoryName}
        titleAction={
          showCategoryInfo && (
            <IconButton
              aria-label={intl.formatMessage({ defaultMessage: "Learn more about {categoryName}" }, { categoryName })}
              size="small"
              onClick={(event) => {
                // Don't toggle the accordion.
                event.stopPropagation()
                toggleInfo()
              }}
            >
              <InfoIcon fontSize="small" sx={(theme) => ({ color: theme.palette.text.secondary })} />
            </IconButton>
          )
        }
      >
        <Stack
          spacing={theme.spacing(theme.tokens.spacing.lg)}
          sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}
        >
          {subsidies.map((subsidy) => (
            <AvailableFundsAmounts
              key={subsidy.benefitType ?? BenefitType.FUND}
              subsidy={subsidy}
              showRemainingAnnual={showRemainingAnnual}
              showRemainingLifetime={showRemainingLifetime}
            />
          ))}
        </Stack>
      </CategoryAccordion>
      {showInfo && (
        <CategoryInfoDialog
          category={subsidies[0].reimbursementCategoryNameMessageKey}
          categoryName={categoryName}
          hasQmeCategory={hasQmeCategory}
          onClose={toggleInfo}
        />
      )}
    </>
  )
}

const AvailableFundsModuleCategories = ({ subsidies }: { subsidies: Subsidy[] }): JSX.Element => {
  const hasMultipleCategories = countCategories(subsidies) > 1
  const theme = useTheme()
  const subsidiesToRender = useMemo(() => getVisibleSubsidies(subsidies), [subsidies])
  const showCategoryInfo = isCreditMember(subsidiesToRender)
  const categoryGroups = useMemo(() => groupByCategory(subsidiesToRender), [subsidiesToRender])
  const hasQmeCategory = getCategoryCoverage(subsidies).qme
  const creditMember = isCreditMember(subsidies)
  const amountSubsidies = creditMember ? subsidiesToRender : subsidies
  const showRemainingAnnual = amountSubsidies.some(
    (subsidy) => subsidy.yearlyAmounts.max !== subsidy.lifetimeAmounts.max
  )
  // Credit plans have no dollar caps, so check for an unlimited paired fund.
  const showRemainingLifetime = amountSubsidies.some(
    (subsidy) =>
      subsidy.lifetimeAmounts.max !== null ||
      (creditMember && subsidy.benefitType !== BenefitType.CREDIT && subsidy.hasUnlimitedBenefit)
  )
  return (
    <>
      <CategoriesContainer>
        {categoryGroups.map((group) =>
          hasMultipleCategories ? (
            <React.Fragment key={group[0].reimbursementCategoryNameMessageKey}>
              <AvailableFundsByCategory
                subsidies={group}
                hasQmeCategory={hasQmeCategory}
                showCategoryInfo={showCategoryInfo}
                showRemainingAnnual={showRemainingAnnual}
                showRemainingLifetime={showRemainingLifetime}
              />
            </React.Fragment>
          ) : (
            <React.Fragment key={group[0].reimbursementCategoryNameMessageKey}>
              <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
                {group.map((subsidy) => (
                  <AvailableFundsAmounts
                    key={subsidy.benefitType ?? BenefitType.FUND}
                    subsidy={subsidy}
                    showRemainingAnnual={showRemainingAnnual}
                    showRemainingLifetime={showRemainingLifetime}
                  />
                ))}
              </Stack>
              {/* One badge set per category, from the dollar plan. */}
              <PlanInfoBadgeContainer
                subsidy={group.find((subsidy) => subsidy.benefitType !== BenefitType.CREDIT) ?? group[0]}
                allSubsidies={subsidiesToRender}
                stackVertically={true}
              />
            </React.Fragment>
          )
        )}
      </CategoriesContainer>
    </>
  )
}

const AvailableFundsModule = ({
  subsidies,
  hasMultipleCategories,
  color = "green"
}: {
  subsidies: Subsidy[]
  hasMultipleCategories: boolean
  color?: SurfaceColor
}): JSX.Element => {
  const theme = useTheme()
  const { hasCarrotPlan } = useCarrotPlan()
  return (
    <Tile variant="secondary" color={color}>
      <Stack
        position="relative"
        sx={{ overflow: "hidden" }}
        id="home-sidebar-available-benefit-module"
        spacing={theme.spacing(theme.tokens.spacing.lg)}
      >
        <Typography variant="h5">
          <FormattedMessage defaultMessage="Available funds" />
        </Typography>
        <Typography variant="body1" color={(theme) => theme.palette.text.secondary}>
          {hasMultipleCategories ? (
            <FormattedMessage defaultMessage="Your benefit includes funds to cover eligible expenses in the following categories." />
          ) : (
            <FormattedMessage defaultMessage="Your benefit includes funds to cover eligible expenses." />
          )}
        </Typography>
        {subsidies && <AvailableFundsModuleCategories subsidies={subsidies} />}
        <Link
          href={hasCarrotPlan ? Paths.FINANCIAL_SUPPORT : DbgPaths.CARROT_FUNDS}
          startIcon={<IconWithBackground icon={MoneyIcon} color={color} />}
          endIcon={<InternalIcon fontSize="small" color="primary" />}
        >
          {hasCarrotPlan ? (
            <FormattedMessage defaultMessage="Access your funds" />
          ) : (
            <FormattedMessage defaultMessage="Learn about taxes and paying for care" />
          )}
        </Link>
      </Stack>
    </Tile>
  )
}

export default AvailableFundsModule
